import { useState } from "react";
import { AiOutlineLoading } from "react-icons/ai";
import { useTranslation } from "react-i18next";

type WorkspaceDialogContentProps = {
  initialName?: string;
  onCancel: () => void;
  onSubmit: (data: { name: string }) => void;
  loading?: boolean;
  error?: string | null;
  isEditMode?: boolean;
};

export function WorkspaceDialogContent({
  initialName = "",
  onCancel,
  onSubmit,
  loading = false,
  error = null,
  isEditMode = false,
}: Readonly<WorkspaceDialogContentProps>) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [touched, setTouched] = useState<{ name: boolean }>({
    name: false,
  });

  const nameError =
    !name.trim() && touched.name ? t("workspaces.dialog.nameRequired") : undefined;

  // For create, disable submit if name is empty
  const isSubmitDisabled = !name.trim();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true });
    if (isSubmitDisabled) {
      return;
    }
    onSubmit({ name: name.trim() });
  };

  return (
    <form
      className="flex min-w-[340px] max-w-[400px] flex-col gap-4"
      onSubmit={handleSubmit}
    >
      <div>
        <h2 className="mb-1 text-xl font-bold">
          {isEditMode ? t("workspaces.dialog.editTitle") : t("workspaces.dialog.createTitle")}
        </h2>
        <p className=" text-sm text-gray-500">
          {isEditMode
            ? t("workspaces.dialog.editDescription")
            : t("workspaces.dialog.createDescription")}
        </p>
      </div>
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium" htmlFor="workspace-name">
          {t("workspaces.dialog.nameLabel")}
        </label>
        <input
          id="workspace-name"
          type="text"
          className={`rounded-md border px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7B3AED] ${
            nameError ? "border-red-400 focus:ring-red-400" : "border-gray-300"
          }`}
          placeholder={t("workspaces.dialog.namePlaceholder")}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!touched.name) setTouched((t) => ({ ...t, name: true }));
          }}
          disabled={loading}
          autoComplete="off"
        />
        {nameError && <span className="text-xs text-red-500">{nameError}</span>}
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-700 transition hover:bg-gray-100"
          onClick={onCancel}
          disabled={loading}
        >
          {t("common.cancel")}
        </button>
        <button
          type="submit"
          className="rounded-md bg-[#7B3AED] px-4 py-2 font-medium text-white transition hover:bg-[#6C22E6] disabled:opacity-60"
          disabled={loading || isSubmitDisabled}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <AiOutlineLoading className="animate-spin" />
              {isEditMode ? t("common.saving") : t("common.creating")}
            </span>
          ) : isEditMode ? (
            t("common.saveChanges")
          ) : (
            t("common.create")
          )}
        </button>
      </div>
    </form>
  );
}
